// src/pages/ThisMonth.js
import React, { useState, useEffect, useMemo } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { useCurrency, getCurrentCurrency } from '../lib/CurrencyContext'
import { theme, atoms, formatMonthYear, firstName } from '../lib/theme'
import MonthlySummary from '../components/MonthlySummary'
import QuickAddTransaction from '../components/QuickAddTransaction'
import WelcomeStrip from '../components/WelcomeStrip'
import CommitmentsCard from '../components/CommitmentsCard'
import { buildPaidMonthSet, resolveUnpaidOccurrence } from '../lib/moneyMovement'

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function ThisMonth({ onboardingCompleted = true, onResumeOnboarding }) {
  const { formatCurrency } = useCurrency()
  // i18n is wired in for future-proofing; chunk 9 strips this import entirely.
  // Keep the import even though we don't call t() — touching index.css's
  // accessibility rules and the layout is enough scope for chunk 2.
  const { partnerId, partnerProfile } = usePartner()
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const openQuickAdd = ctx.openQuickAdd

  // ─── Date / month context (allow stepping back/forward) ───
  const today = useMemo(() => new Date(), [])
  const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1))
  const month = viewDate.getMonth() + 1
  const year = viewDate.getFullYear()
  const isCurrentMonth = month === today.getMonth() + 1 && year === today.getFullYear()
  const isPastMonth = viewDate < new Date(today.getFullYear(), today.getMonth(), 1)
  const daysInMonth = new Date(year, month, 0).getDate()
  const daysPassed = isCurrentMonth ? today.getDate() : daysInMonth
  const daysRemaining = isCurrentMonth ? Math.max(0, daysInMonth - today.getDate()) : 0

  // ─── Data state ───────────────────────────────────────────
  const [profile, setProfile] = useState(null)
  const [budgetTotal, setBudgetTotal] = useState(0)
  const [budgetLockedAt, setBudgetLockedAt] = useState(null)
  const [spent, setSpent] = useState(0)
  const [bills, setBills] = useState([])
  const [categories, setCategories] = useState([])
  // All user categories indexed by id (across budgets) -> { name, parentName }.
  // Used to label each upcoming bill with its parent category in "Coming up".
  const [catIndex, setCatIndex] = useState({})
  const [transactions, setTransactions] = useState([])
  const [accountsTotal, setAccountsTotal] = useState(0)
  const [accounts, setAccounts] = useState([]) // Chunk 7 — for "Money to move" account names
  const [billPaidMonthSet, setBillPaidMonthSet] = useState(new Set()) // Chunk 7 follow-up — matched-payment months
  const [debtsTotal, setDebtsTotal] = useState(0)
  const [accountsLastUpdated, setAccountsLastUpdated] = useState(null)
  const [savingsGoals, setSavingsGoals] = useState([])
  const [debtsList, setDebtsList] = useState([])
  const [hasIncome, setHasIncome] = useState(false)
  const [monthlyIncome, setMonthlyIncome] = useState(0)
  const [incomeSourcesThisMonth, setIncomeSourcesThisMonth] = useState([])
  const [hasAccounts, setHasAccounts] = useState(false)
  const [hasBills, setHasBills] = useState(false)
  const [hasTransactions, setHasTransactions] = useState(false)
  const [hasPlan, setHasPlan] = useState(false)
  const [welcomeDismissed, setWelcomeDismissed] = useState(true)
  const [hasPartner, setHasPartner] = useState(false)
  const [loading, setLoading] = useState(true)

  // ─── UI state ─────────────────────────────────────────────
  const [showMonthlySummary, setShowMonthlySummary] = useState(false)
  const [checklistDismissed, setChecklistDismissed] = useState(false)
  const [paidBillIds, setPaidBillIds] = useState(new Set())
  const [paymentMethods, setPaymentMethods] = useState([])
  const [markPaidBill, setMarkPaidBill] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  const [timeToPlanDismissed, setTimeToPlanDismissed] = useState(false)

  // ─── Effects ──────────────────────────────────────────────
  useEffect(() => {
    const dismissed = localStorage.getItem('checklist_dismissed')
    if (dismissed) setChecklistDismissed(true)
  }, [])

  // "Time to plan" banner dismissal is per-user-per-month (v2 chunk 4). Re-read
  // whenever the user or viewed month changes so navigating months shows the
  // banner again for any month that hasn't been dismissed or locked.
  const timeToPlanKey = profile?.id ? `vault.timeToPlanDismissed.${profile.id}.${year}-${month}` : null
  useEffect(() => {
    if (!timeToPlanKey) { setTimeToPlanDismissed(false); return }
    setTimeToPlanDismissed(localStorage.getItem(timeToPlanKey) === '1')
  }, [timeToPlanKey])
  const dismissTimeToPlan = () => {
    if (timeToPlanKey) localStorage.setItem(timeToPlanKey, '1')
    setTimeToPlanDismissed(true)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [partnerId, month, year, refreshTick])
  // Listen for any data change that affects this page's view and refetch.
  useDataEvent(
    [
      EVENTS.TRANSACTION_SAVED,
      EVENTS.TRANSACTION_UPDATED,
      EVENTS.TRANSACTION_DELETED,
      EVENTS.BUDGET_SAVED,
      EVENTS.CATEGORY_SAVED,
      EVENTS.CATEGORY_UPDATED,
      EVENTS.CATEGORY_DELETED,
      EVENTS.BILL_SAVED,
      EVENTS.BILL_UPDATED,
      EVENTS.BILL_DELETED,
      EVENTS.INCOME_SAVED,
      EVENTS.INCOME_UPDATED,
      EVENTS.INCOME_DELETED,
      EVENTS.DEBT_SAVED,
      EVENTS.DEBT_UPDATED,
      EVENTS.DEBT_DELETED,
      EVENTS.GOAL_SAVED,
      EVENTS.GOAL_UPDATED,
      EVENTS.GOAL_DELETED,
      EVENTS.ACCOUNT_SAVED,
      EVENTS.ACCOUNT_UPDATED,
      EVENTS.ACCOUNT_DELETED,
      EVENTS.ACCOUNT_BALANCE_UPDATED,
      EVENTS.PROFILE_UPDATED,
    ],
    () => setRefreshTick(t => t + 1)
  )

    // ─── Data fetch ─────────────────────────────────────────
  // Single-roundtrip waterfall: ONE Promise.all to get the budget +
  // base data, then ONE Promise.all to get tx/cats/paid/pms after we
  // know the budget id. Removes the duplicate profiles + budgets
  // queries from the original implementation.
  const fetchData = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    // ─── Round 1: everything that doesn't depend on budget.id ──
    const [
      profileRes,
      budgetRes,
      billsRes,
      accountsRes,
      debtsRes,
      goalsRes,
      incomeAnyRes,
      incomeMonthRes,
      txCountRes,
    ] = await Promise.all([
      supabase.from('profiles')
        .select('id, full_name, partner_id, welcome_dismissed_at, currency_code, currency_locale, currency_symbol')
        .eq('id', user.id).maybeSingle(),
      supabase.from('budgets')
        .select('*')
        .in('user_id', userIds)
        .eq('month', month).eq('year', year).eq('is_active', true)
        .order('created_at').limit(1).maybeSingle(),
      supabase.from('bills')
        .select('*')
        .in('user_id', userIds)
        .eq('is_active', true)
        .order('due_day'),
      supabase.from('accounts').select('*').in('user_id', userIds),
      supabase.from('debts').select('*').in('user_id', userIds),
      supabase.from('savings_goals').select('*').in('user_id', userIds).order('created_at'),
      // Onboarding check: any income source ever
      supabase.from('income_sources').select('id').eq('user_id', user.id).eq('is_active', true).limit(1),
      // Income for THIS specific month/year — needed for hero math
      supabase.from('income_sources')
        .select('amount, frequency, name, source_type')
        .in('user_id', userIds)
        .eq('is_active', true)
        .eq('month', month).eq('year', year),
      supabase.from('transactions').select('id').eq('user_id', user.id).limit(1),
    ])

    const profileData = profileRes.data
    if (profileData) {
      setProfile(profileData)
      setHasPartner(!!profileData.partner_id)
      setWelcomeDismissed(!!profileData.welcome_dismissed_at)
    }

    setHasIncome((incomeAnyRes.data || []).length > 0)

    // Sum this month's income (with frequency normalization, like Plan does)
    const monthIncomeList = incomeMonthRes.data || []
    setIncomeSourcesThisMonth(monthIncomeList)
    const monthIncomeTotal = monthIncomeList.reduce((sum, src) => {
      const a = parseFloat(src.amount || 0)
      switch (src.frequency) {
        case 'weekly':    return sum + a * 4.33
        case 'biweekly':  return sum + a * 2.17
        case 'quarterly': return sum + a / 3
        case 'annual':    return sum + a / 12
        default:          return sum + a
      }
    }, 0)
    setMonthlyIncome(monthIncomeTotal)
    setHasTransactions((txCountRes.data || []).length > 0)
    setBills(billsRes.data || [])
    setHasBills((billsRes.data || []).length > 0)

    // Accounts (assets) summary
    setAccounts(accountsRes.data || [])
    if (accountsRes.data) {
      const assets = accountsRes.data
        .filter(a => a.is_asset)
        .reduce((sum, a) => sum + parseFloat(a.current_balance || 0), 0)
      setAccountsTotal(assets)
      setHasAccounts(accountsRes.data.length > 0)
      const latest = accountsRes.data
        .map(a => a.last_updated)
        .filter(Boolean)
        .sort()
        .pop()
      if (latest) setAccountsLastUpdated(latest)
    }

    // Debts summary
    if (debtsRes.data) {
      setDebtsTotal(debtsRes.data.reduce((sum, d) => sum + parseFloat(d.current_balance || 0), 0))
      setDebtsList(debtsRes.data)
    }

    setSavingsGoals(goalsRes.data || [])

    // Compute month boundaries for the next round of queries
    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`
    const monthEnd = month === 12
      ? `${year + 1}-01-01`
      : `${year}-${String(month + 1).padStart(2, '0')}-01`

    // ─── Round 2: everything that depends on budget.id (or is otherwise
    //     parallelizable). One Promise.all instead of three serial awaits.
    const budget = budgetRes.data
    setBudgetLockedAt(budget?.locked_at || null)
    // Money Movement paid-status window: matched payments across the current
    // month + the next two, so an occurrence in a later month can be recognised
    // as paid (Chunk 7 follow-up).
    const mmEnd = `${month + 3 > 12 ? year + 1 : year}-${String(((month - 1 + 3) % 12) + 1).padStart(2, '0')}-01`
    const [paidRes, pmRes, txRes, catsRes, mmPaidRes] = await Promise.all([
      // Paid bill ids this month
      supabase.from('transactions').select('bill_id')
        .in('user_id', userIds)
        .not('bill_id', 'is', null)
        .gte('transaction_date', monthStart).lt('transaction_date', monthEnd),
      // Payment methods
      supabase.from('payment_methods').select('*').in('user_id', userIds),
      // Transactions for this budget (only if budget exists)
      budget
        ? supabase.from('transactions')
            .select('id, amount, category_id, transaction_date, merchant_name, type')
            .in('user_id', userIds)
            .eq('budget_id', budget.id)
            .eq('type', 'expense')
            .order('transaction_date', { ascending: false })
        : Promise.resolve({ data: [] }),
      // Categories for this budget (only if budget exists)
      budget
        ? supabase.from('budget_categories')
            .select('*')
            .eq('user_id', user.id)
            .eq('budget_id', budget.id)
        : Promise.resolve({ data: [] }),
      // Matched payments across the money-movement paid-status window
      supabase.from('transactions').select('bill_id, transaction_date')
        .in('user_id', userIds)
        .not('bill_id', 'is', null)
        .gte('transaction_date', monthStart).lt('transaction_date', mmEnd),
    ])

    setPaidBillIds(new Set((paidRes.data || []).map(t => t.bill_id)))
    setBillPaidMonthSet(buildPaidMonthSet(mmPaidRes.data || []))
    setPaymentMethods(pmRes.data || [])

    if (budget) {
      // Allocated = budget categories + monthly goal contributions + monthly debt payments.
      // Every dollar accounted for, YNAB-style.
      // Sum category limits (only top-level categories — subcategories already roll up to their parent's limit)
      const catList = catsRes.data || []
      // Sum subcategory limits — parent categories have limit_amount = 0
      // by design (they're "computed" from their children's allocations).
      const baseTotal = catList
        .filter(c => c.parent_id)
        .reduce((s, c) => s + parseFloat(c.limit_amount || 0), 0)
      const goalsAlloc = (goalsRes.data || []).reduce(
        (s, g) => s + parseFloat(g.monthly_contribution || 0), 0
      )
      const debtsAlloc = (debtsRes.data || []).reduce(
        (s, d) => s + parseFloat(d.monthly_payment || 0), 0
      )
      const billsAlloc = (billsRes.data || []).reduce(
        (s, b) => s + parseFloat(b.amount || 0), 0
      )
      setBudgetTotal(baseTotal + goalsAlloc + debtsAlloc + billsAlloc)

      const txList = txRes.data || []
      setTransactions(txList)
      setSpent(txList.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0))

      setCategories(catList)
      // Plan exists if any category has a non-zero limit
      // "Has plan" means: either category limits are set, OR transactions exist for this month.
      // Past months with imported data should feel "planned" even without category limits set,
      // because the user is just looking back at history.
      const hasLimits = catList.some(c => parseFloat(c.limit_amount || 0) > 0)
      const hasTransactions = (txRes.data || []).length > 0
      setHasPlan(hasLimits || hasTransactions)
    } else {
      setBudgetTotal(0)
      setSpent(0)
      setTransactions([])
      setCategories([])
      setHasPlan(false)
    }

    // Index all of the user's categories (across budgets) so each upcoming bill
    // can show its parent category name in "Coming up". Bills' category_id may
    // point to a subcategory in another budget; we just need its name + parent.
    const allCatsRes = await supabase.from('budget_categories')
      .select('id, name, parent_id').in('user_id', userIds)
    const allCats = allCatsRes.data || []
    const byId = {}
    allCats.forEach(c => { byId[c.id] = c })
    const idx = {}
    allCats.forEach(c => {
      idx[c.id] = { name: c.name, parentName: c.parent_id ? (byId[c.parent_id]?.name || null) : null }
    })
    setCatIndex(idx)

    setLoading(false)
  }

  // ─── Derived values ───────────────────────────────────────
  const remaining = budgetTotal - spent
  const spentPct = budgetTotal > 0 ? Math.min((spent / budgetTotal) * 100, 100) : 0
  const monthPct = isCurrentMonth ? Math.min((daysPassed / daysInMonth) * 100, 100) : 100

  const projectedSpend = daysPassed > 0 ? (spent / daysPassed) * daysInMonth : 0
  const onPace = budgetTotal === 0 || projectedSpend <= budgetTotal * 1.05
  const overPace = projectedSpend > budgetTotal * 1.15

  const netWorth = accountsTotal - debtsTotal

  // Spent per category map
  const spentByCat = useMemo(() => {
    const m = new Map()
    transactions.forEach(tx => {
      if (tx.category_id) {
        m.set(tx.category_id, (m.get(tx.category_id) || 0) + parseFloat(tx.amount || 0))
      }
    })
    return m
  }, [transactions])

  // Categories to watch — over budget OR pacing high
  const watchCategories = useMemo(() => {
    const pctMonth = isCurrentMonth ? daysPassed / daysInMonth : 1
    return categories
      .filter(c => parseFloat(c.limit_amount || 0) > 0)
      .map(cat => {
        const limit = parseFloat(cat.limit_amount || 0)
        const catSpent = spentByCat.get(cat.id) || 0
        const pctSpent = limit > 0 ? catSpent / limit : 0
        let kind = null
        if (pctSpent >= 1.0) kind = 'over'
        else if (pctSpent >= 0.8 && pctSpent > pctMonth + 0.2) kind = 'warn'
        else if (pctSpent > pctMonth + 0.3) kind = 'pace'
        return { cat, limit, catSpent, pctSpent, kind }
      })
      .filter(x => x.kind)
      .sort((a, b) => b.pctSpent - a.pctSpent)
      .slice(0, 4)
  }, [categories, spentByCat, daysPassed, daysInMonth, isCurrentMonth])

  // Bills coming up — current-month bills, plus next-month bills inside
  // their reminder window (so the user gets the early warning they asked
  // for in plan setup, but isn't shown irrelevant May bills in mid-April).
  const upcomingBills = useMemo(() => {
    if (!isCurrentMonth) return bills.filter(b => !paidBillIds.has(b.id)).slice(0, 3)
    const day = today.getDate()
    const daysLeftInMonth = daysInMonth - day

    const eligible = bills.filter(b => {
      // Already paid this month → graduate out of "Coming up"
      if (paidBillIds.has(b.id)) return false
      // Bill due later this month → always include
      if (b.due_day >= day) return true
      // Bill due next month → include only if its reminder window has opened
      const reminderDays = parseInt(b.reminder_days) || 3
      const daysUntilNextOccurrence = daysLeftInMonth + b.due_day
      return daysUntilNextOccurrence <= reminderDays
    })

    const sorted = eligible.sort((a, b) => {
      const da = a.due_day >= day ? a.due_day - day : daysLeftInMonth + a.due_day
      const db = b.due_day >= day ? b.due_day - day : daysLeftInMonth + b.due_day
      return da - db
    })
    return sorted.slice(0, 3)
  }, [bills, isCurrentMonth, today, daysInMonth, paidBillIds])

  // Money to move (Chunk 7) — bills with a "Pays from" account due in the next
  // 14 days, grouped by account. Advice-only: obligation-based, not paid-status
  // aware (spec §9). Bills without an account_id are excluded by design.
  const moneyToMove = useMemo(() => {
    if (!isCurrentMonth) return []
    const acctById = {}
    accounts.forEach(a => { acctById[a.id] = a })
    const groups = {}
    bills.forEach(b => {
      if (!b.account_id) return
      // Skip bills already paid for their upcoming period (Chunk 7 follow-up).
      const occ = resolveUnpaidOccurrence(b, today, 14, billPaidMonthSet)
      if (!occ) return
      const g = groups[b.account_id] || (groups[b.account_id] = {
        accountId: b.account_id,
        accountName: acctById[b.account_id]?.name || 'account',
        total: 0, bills: [], earliestDays: Infinity,
      })
      g.total += parseFloat(b.amount || 0)
      g.bills.push(b)
      if (occ.offset < g.earliestDays) g.earliestDays = occ.offset
    })
    return Object.values(groups).sort((a, b) => a.earliestDays - b.earliestDays)
  }, [bills, accounts, today, isCurrentMonth, billPaidMonthSet])

  // Parent category name for an upcoming bill (Bill-Inside-Category Rule).
  // Returns the parent category's name (or the category's own name if a bill is
  // assigned directly to a parent); null when the bill has no/unknown category.
  const billCategoryLabel = (bill) => {
    if (!bill.category_id) return null
    const e = catIndex[bill.category_id]
    if (!e) return null
    return e.parentName || e.name || null
  }

  // This month's plan: top goal + top debt with progress this month
  const planGoal = savingsGoals[0] || null
  const planDebt = debtsList[0] || null

  // ─── Checklist ───────────────────────────────────────────
  const checklistItems = [
    { done: hasIncome,       label: 'Add your monthly income', sub: 'So we can track your savings rate', link: '/plan',     cta: 'Set income' },
    { done: hasBills,        label: 'Add your recurring bills', sub: 'Rent, subscriptions, utilities',     link: '/log',      cta: 'Add bills' },
    { done: hasAccounts,     label: 'Add your accounts',        sub: 'Track net worth without a bank login', link: '/networth', cta: 'Add accounts' },
    { done: hasTransactions, label: 'Log your first transaction', sub: 'Takes 10 seconds — amount, category, done', link: '/log',  cta: 'Add transaction' },
    { done: hasPartner,      label: 'Invite your partner',      sub: 'Share budgets, bills, and net worth', link: '/settings', cta: 'Invite' },
  ]
  const checklistDone = checklistItems.filter(i => i.done).length
  const allDone = checklistDone === checklistItems.length
  const showChecklist = !checklistDismissed && !allDone && !onboardingCompleted

  const handleDismissChecklist = () => {
    localStorage.setItem('checklist_dismissed', 'true')
    setChecklistDismissed(true)
  }

  // ─── Month nav ───────────────────────────────────────────
  const stepMonth = (delta) => {
    const next = new Date(viewDate)
    next.setMonth(next.getMonth() + delta)
    setViewDate(next)
  }

  // ─── Render ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.loading}>
        <span>Loading your month…</span>
      </div>
    )
  }

  const greetingName = firstName(profile?.full_name) || 'there'

  // Format hero amount: split currency / integer / cents for typographic display
  const heroParts = splitCurrency(remaining, profile)
  const sharingFirst = partnerProfile ? firstName(partnerProfile.full_name) : null

  // ─── Past-month branch: render historical view instead ────
  if (isPastMonth) {
    return (
      <PastMonthLedger
        viewDate={viewDate}
        isMobile={isMobile}
        profile={profile}
        partnerProfile={partnerProfile}
        monthlyIncome={monthlyIncome}
        incomeSources={incomeSourcesThisMonth}
        spent={spent}
        transactions={transactions}
        categories={categories}
        bills={bills}
        paidBillIds={paidBillIds}
        formatCurrency={formatCurrency}
        onPrevMonth={() => stepMonth(-1)}
        onNextMonth={() => stepMonth(1)}
        showMonthlySummary={showMonthlySummary}
        onShowMonthlySummary={() => setShowMonthlySummary(true)}
        onCloseMonthlySummary={() => setShowMonthlySummary(false)}
      />
    )
  }

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>

      {/* Welcome strip (chunk 8) */}
      {!welcomeDismissed && profile && (
        <WelcomeStrip
          profile={profile}
          hasIncome={hasIncome}
          hasPlan={hasPlan}
          hasTransactions={hasTransactions}
          onChange={() => { fetchData() }}
        />
      )}

      {/* MonthlySummary modal — opened by "📊 Last month" button */}
      {showMonthlySummary && (
        <MonthlySummary onDismiss={() => setShowMonthlySummary(false)} />
      )}

      {/* ─── "Time to plan" banner (v2 chunk 4) ─────────────────
          Shows on the current month while its plan isn't locked. Persistent
          across visits until the user locks the plan (locked_at set) or
          dismisses it (per-user-per-month localStorage). */}
      {isCurrentMonth && !loading && !budgetLockedAt && !timeToPlanDismissed && (
        <div style={styles.timeToPlan}>
          <div style={styles.timeToPlanText}>
            <div style={styles.timeToPlanTitle}>
              Time to plan {viewDate.toLocaleString('default', { month: 'long' })}
            </div>
            <div style={styles.timeToPlanSub}>
              Your plan for {viewDate.toLocaleString('default', { month: 'long' })} hasn’t been locked in yet.
            </div>
          </div>
          <div style={styles.timeToPlanActions}>
            <Link to="/plan" className="btn-compact" style={styles.timeToPlanCta}>
              Review plan
            </Link>
            <button
              type="button"
              className="btn-compact"
              style={styles.timeToPlanDismiss}
              onClick={dismissTimeToPlan}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ─── Page top: context line + month nav ─────────── */}
      <div style={isMobile ? styles.pageTopMobile : styles.pageTop}>
        {isMobile ? (
          <>
            <div style={styles.greetingMobile}>
              <h1 style={styles.greetingHi}>Hey {greetingName}</h1>
              <div style={styles.greetingMeta}>
                <span>{viewDate.toLocaleString('default', { month: 'long' })}</span>
                {isCurrentMonth && (
                  <>
                    <span style={atoms.pageMetaDot} />
                    <span>{daysRemaining} days left</span>
                  </>
                )}
                {sharingFirst && (
                  <>
                    <span style={atoms.pageMetaDot} />
                    <span style={styles.sharingMobile}>with {sharingFirst}</span>
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              className="btn-compact"
              style={styles.historyBtn}
              onClick={() => setShowMonthlySummary(true)}
              aria-label="View last month summary"
            >
              Last month
            </button>
          </>
        ) : (
          <>
            <div style={atoms.pageMeta}>
              <span style={{ color: theme.colors.plum, fontWeight: 600 }}>
                {formatMonthYear(viewDate)}
              </span>
              {isCurrentMonth && (
                <>
                  <span style={atoms.pageMetaDot} />
                  <span>{daysRemaining} days remaining</span>
                </>
              )}
              {sharingFirst && (
                <>
                  <span style={atoms.pageMetaDot} />
                  <span style={atoms.sharingPill}>
                    Sharing with {sharingFirst}
                  </span>
                </>
              )}
            </div>

            <div style={styles.headerRight}>
              <button
                type="button"
                className="btn-compact"
                style={styles.lastMonthBtn}
                onClick={() => setShowMonthlySummary(true)}
              >
                Last month
              </button>
              <div style={styles.monthNav}>
                <button
                  type="button"
                  className="btn-compact"
                  style={styles.monthNavBtn}
                  onClick={() => stepMonth(-1)}
                  aria-label="Previous month"
                  title="Previous month"
                >
                  <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>‹</span>
                </button>
                <button
                  type="button"
                  className="btn-compact"
                  style={styles.monthNavBtn}
                  onClick={() => stepMonth(1)}
                  aria-label="Next month"
                  title="Next month"
                  disabled={isCurrentMonth}
                >
                  <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>›</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Onboarding checklist (only if incomplete) ──── */}
      {showChecklist && (
        <div style={styles.checklist}>
          <div style={styles.checklistHeader}>
            <div>
              <div style={styles.checklistTitle}>Get started with Merq Vault</div>
              <div style={styles.checklistProgress}>{checklistDone} of {checklistItems.length} complete</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {onResumeOnboarding && (
                <button
                  type="button"
                  className="btn-compact"
                  style={styles.checklistResume}
                  onClick={onResumeOnboarding}
                >
                  Resume tour
                </button>
              )}
              <button
                type="button"
                className="btn-compact"
                style={styles.checklistDismiss}
                onClick={handleDismissChecklist}
                aria-label="Dismiss checklist"
              >
                ×
              </button>
            </div>
          </div>
          <div style={styles.checklistBar}>
            <div style={{ ...styles.checklistBarFill, width: `${(checklistDone / checklistItems.length) * 100}%` }} />
          </div>
          <div style={styles.checklistItems}>
            {checklistItems.map((item, i) => (
              <div key={i} style={{ ...styles.checklistItem, opacity: item.done ? 0.55 : 1 }}>
                <div style={{
                  ...styles.checklistCheck,
                  background: item.done ? theme.colors.green : 'transparent',
                  border: item.done ? 'none' : `1.5px solid ${theme.colors.lineStrong}`,
                  color: item.done ? '#fff' : theme.colors.inkQuiet,
                }}>
                  {item.done && <span aria-hidden="true" style={{ fontSize: '12px', lineHeight: 1 }}>✓</span>}
                </div>
                <div style={styles.checklistItemText}>
                  <div style={{ ...styles.checklistItemLabel, textDecoration: item.done ? 'line-through' : 'none' }}>
                    {item.label}
                  </div>
                  <div style={styles.checklistItemSub}>{item.sub}</div>
                </div>
                {!item.done && (
                  <Link to={item.link} className="btn-compact" style={styles.checklistCta}>
                    {item.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Hero: left to spend ────────────────────────── */}
      <section style={isMobile ? styles.heroMobile : styles.hero}>
        {isMobile ? (
          <>
            <div style={styles.heroLabelRowMobile}>
              <span style={styles.heroLabel}>Left to spend</span>
              <PaceTag onPace={onPace} overPace={overPace} budgetTotal={budgetTotal} />
            </div>
            <div style={styles.heroAmountMobile}>
              <span style={styles.heroCurrencyMobile}>{heroParts.symbol}</span>
              {heroParts.whole}
            </div>
            <div style={styles.heroSubMobile}>
              <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{formatCurrency(spent)}</strong>
              {' '}spent of{' '}
              <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{formatCurrency(budgetTotal)}</strong>
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${spentPct}%` }} />
              {isCurrentMonth && <div style={{ ...styles.progressMarker, left: `${monthPct}%` }} />}
            </div>
            <div style={styles.heroFoot}>
              <span>Day {daysPassed} of {daysInMonth}</span>
              <span>{spentPct.toFixed(1)}% used</span>
            </div>
          </>
        ) : (
          <>
            <div style={styles.heroLabel}>Left to spend</div>
            <div style={atoms.amountDisplay}>
              <span style={atoms.amountCurrency}>{heroParts.symbol}</span>
              {heroParts.whole}
              <span style={atoms.amountCents}>.{heroParts.cents}</span>
            </div>
            <div style={styles.heroSub}>
              <span>of <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{formatCurrency(budgetTotal)}</strong> allocated</span>
              <span style={atoms.pageMetaDot} />
              <span><strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{formatCurrency(spent)}</strong> spent so far</span>
              <PaceTag onPace={onPace} overPace={overPace} budgetTotal={budgetTotal} />
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${spentPct}%` }} />
              {isCurrentMonth && <div style={{ ...styles.progressMarker, left: `${monthPct}%` }} />}
            </div>
          </>
        )}
      </section>
      <CommitmentsCard />

      {/* ─── Empty-budget CTA ────────────────────────────── */}
      {budgetTotal === 0 && !hasPlan && (
        <div style={styles.emptyBudget}>
          <div>
            <div style={styles.emptyBudgetTitle}>No plan yet for {viewDate.toLocaleString('default', { month: 'long' })}</div>
            <div style={styles.emptyBudgetSub}>Set up this month's budget to start tracking.</div>
          </div>
          <Link to="/plan" className="btn-compact" style={styles.emptyBudgetCta}>
            Build the plan
          </Link>
        </div>
      )}

      {/* ─── Coming up: bills ───────────────────────────── */}
      {upcomingBills.length > 0 && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>Coming up</h2>
            <Link to="/log?tab=bills" className="btn-compact" style={styles.rowLink}>
              All bills            </Link>
          </div>
          {isMobile ? (
            <div style={styles.cardList}>
              {upcomingBills.map(bill => {
                const days = daysUntil(bill.due_day, today)
                const soon = days <= 4
                const isPaid = paidBillIds.has(bill.id)
                return (
                  <div
                    key={bill.id}
                    style={styles.listCardMobile}
                    onClick={() => { if (!isPaid) setMarkPaidBill(bill) }}
                  >
                    <div style={styles.billDate}>
                      <span style={styles.billDateNum}>{bill.due_day}</span>
                      <span style={styles.billDateMo}>{nextMonthShort(today, bill.due_day)}</span>
                    </div>
                    <div style={styles.billInfo}>
                      <div style={styles.billName}>{bill.name}</div>
                      <div style={{
                        ...styles.billMeta,
                        color: isPaid ? theme.colors.green : (soon ? theme.colors.orange : theme.colors.inkMuted),
                        fontWeight: (isPaid || soon) ? 600 : 500,
                      }}>
                        {billCategoryLabel(bill) || '(no category)'} · {isPaid ? '✓ Paid' : dueLabel(days)}
                      </div>
                    </div>
                    <div style={styles.billAmount}>{formatCurrency(bill.amount)}</div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={atoms.card}>
              {upcomingBills.map((bill, idx) => {
                const days = daysUntil(bill.due_day, today)
                const soon = days <= 4
                const last = idx === upcomingBills.length - 1
                const isPaid = paidBillIds.has(bill.id)
                return (
                  <div
                    key={bill.id}
                    style={{
                      ...styles.billRow,
                      borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                    }}
                  >
                    <div style={styles.billDate}>
                      <span style={styles.billDateNum}>{bill.due_day}</span>
                      <span style={styles.billDateMo}>{nextMonthShort(today, bill.due_day)}</span>
                    </div>
                    <div style={styles.billInfo}>
                      <div style={styles.billName}>{bill.name}</div>
                      <div style={styles.billMeta}>
                        {[billCategoryLabel(bill) || '(no category)', friendlyFreq(bill.frequency), bill.notes]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div style={{
                      ...styles.billDue,
                      color: isPaid ? theme.colors.green : (soon ? theme.colors.orange : theme.colors.inkMuted),
                      fontWeight: (isPaid || soon) ? 600 : 500,
                    }}>
                      {isPaid ? '✓ Paid' : dueLabel(days)}
                    </div>
                    <div style={styles.billAmount}>{formatCurrency(bill.amount)}</div>
                    {!isPaid && (
                      <button
                        type="button"
                        className="btn-compact"
                        style={styles.markPaidBtn}
                        onClick={() => setMarkPaidBill(bill)}
                      >
                        Mark paid
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {/* ─── Money to move (Chunk 7) ────────────────────── */}
      {moneyToMove.length > 0 && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>Money to move</h2>
            <Link to="/money-movement" className="btn-compact" style={styles.rowLink}>
              See 30-day view →
            </Link>
          </div>
          <div style={atoms.card}>
            {moneyToMove.map((g, idx) => {
              const last = idx === moneyToMove.length - 1
              return (
                <div
                  key={g.accountId}
                  style={{
                    ...styles.moveRow,
                    borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                  }}
                >
                  <div style={styles.moveHead}>
                    <span style={styles.moveAmount}>{formatCurrency(g.total)}</span>
                    {' '}needs to be in{' '}
                    <span style={styles.moveAccount}>{g.accountName}</span>
                    {' '}by {byDateLabel(g.earliestDays, today)}
                  </div>
                  <div style={styles.moveBills}>
                    {g.bills.map(b => (
                      <span key={b.id} style={styles.moveBillChip}>
                        {b.name} · {formatCurrency(b.amount)}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* ─── Categories to watch ────────────────────────── */}
      {watchCategories.length > 0 && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>{isMobile ? 'Watch' : 'Categories to watch'}</h2>
            <Link to="/plan" className="btn-compact" style={styles.rowLink}>
              {isMobile ? 'Full plan →' : 'Full budget'}
            </Link>
          </div>
          {isMobile ? (
            <div style={styles.cardList}>
              {watchCategories.map(({ cat, limit, catSpent, pctSpent, kind }) => (
                <div key={cat.id} style={styles.catCardMobile}>
                  <div style={styles.catRowTop}>
                    <span style={styles.catName}>{cat.name}</span>
                    <span style={styles.catAmount}>
                      {formatCurrency(catSpent)}{' '}
                      <span style={{ color: theme.colors.inkQuiet, fontWeight: 500 }}>/ {formatCurrency(limit)}</span>
                    </span>
                  </div>
                  <div style={styles.catBar}>
                    <div style={{
                      ...styles.catBarFill,
                      width: `${Math.min(pctSpent * 100, 100)}%`,
                      background: theme.colors.orange,
                      opacity: kind === 'warn' || kind === 'pace' ? 0.7 : 1,
                    }} />
                  </div>
                  <div style={{ ...styles.catStatusMobile, color: theme.colors.orange }}>
                    {kind === 'over'
                      ? `${formatCurrency(catSpent - limit)} over budget`
                      : kind === 'warn'
                        ? `${Math.round(pctSpent * 100)}% used · ${daysRemaining} days left`
                        : `Pace high · ${Math.round(pctSpent * 100)}% used`}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={atoms.card}>
              {watchCategories.map(({ cat, limit, catSpent, pctSpent, kind }, idx) => {
                const last = idx === watchCategories.length - 1
                return (
                  <div
                    key={cat.id}
                    style={{
                      ...styles.catRow,
                      borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                    }}
                  >
                    <div style={styles.catNameWrap}>
                      <span style={styles.catLabel}>{cat.name}</span>
                      <div style={styles.catBar}>
                        <div style={{
                          ...styles.catBarFill,
                          width: `${Math.min(pctSpent * 100, 100)}%`,
                          background: theme.colors.orange,
                          opacity: kind === 'over' ? 1 : 0.7,
                        }} />
                      </div>
                    </div>
                    <div style={styles.catSpend}>
                      {formatCurrency(catSpent)}{' '}
                      <span style={{ color: theme.colors.inkQuiet, fontWeight: 500 }}>/ {formatCurrency(limit)}</span>
                    </div>
                    <div style={{ ...styles.catStatus, color: theme.colors.orange }}>
                      {kind === 'over' ? `${formatCurrency(catSpent - limit)} over` : 'Pace high'}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {/* ─── On track this month: goal + debt ────────────── */}
      {!isMobile && (planGoal || planDebt) && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>On track this month</h2>
            <Link to="/plan" className="btn-compact" style={styles.rowLink}>
              All plans            </Link>
          </div>
          <div style={styles.planGrid}>
            {planGoal && (
              <PlanCard
                kicker="Savings goal"
                name={planGoal.name}
                detail={planGoal.target_date
                  ? `Target ${formatCurrency(planGoal.target_amount)} by ${new Date(planGoal.target_date).toLocaleDateString('default', { month: 'short', year: 'numeric' })}`
                  : `Target ${formatCurrency(planGoal.target_amount)}`}
                progress={planGoal.target_amount > 0 ? planGoal.current_amount / planGoal.target_amount : 0}
                progressLabel={`${formatCurrency(planGoal.current_amount)} of ${formatCurrency(planGoal.target_amount)}`}
                color="purple"
              />
            )}
            {planDebt && (
              <PlanCard
                kicker="Debt payoff"
                name={planDebt.name}
                detail={planDebt.monthly_payment
                  ? `${formatCurrency(planDebt.monthly_payment)}/mo planned`
                  : 'Track payments'}
                progress={planDebt.original_balance > 0
                  ? Math.max(0, 1 - (planDebt.current_balance / planDebt.original_balance))
                  : 0}
                progressLabel={`${formatCurrency(planDebt.original_balance - planDebt.current_balance)} of ${formatCurrency(planDebt.original_balance)} paid`}
                color="green"
              />
            )}
          </div>
        </section>
      )}

      {/* ─── Footer line: net worth ─────────────────────── */}
      <div style={isMobile ? styles.nwFooterMobile : styles.footerLine}>
        <div>
          <span style={{ color: theme.colors.inkMuted }}>Net worth: </span>
          <span style={styles.nwAmount}>{formatCurrency(netWorth)}</span>
          {accountsLastUpdated && !isMobile && (
            <>
              <span style={{ color: theme.colors.inkQuiet, margin: '0 10px' }}>·</span>
              <span style={{ color: theme.colors.inkMuted }}>
                updated {new Date(accountsLastUpdated).toLocaleDateString('default', { month: 'short', day: 'numeric' })}
              </span>
            </>
          )}
        </div>
        <Link to="/networth" className="btn-compact" style={styles.nwLink}>
          {isMobile ? 'Update' : 'Update balances'}        </Link>
      </div>

      {/* ─── Desktop FAB: add transaction ────────────────── */}
      {!isMobile && openQuickAdd && (
        <button
          type="button"
          style={styles.fab}
          onClick={openQuickAdd}
          aria-label="Add transaction"
        >
          Add transaction
        </button>
      )}

      {/* Mark paid sheet */}
      {markPaidBill && (
        <MarkPaidSheet
          bill={markPaidBill}
          paymentMethods={paymentMethods}
          onClose={() => setMarkPaidBill(null)}
          onSaved={() => {
            setMarkPaidBill(null)
            setRefreshTick(t => t + 1)
          }}
        />
      )}
    </div>
  )
}

// ─── Mark Paid sheet ──────────────────────────────────────────
// Creates a transaction with bill_id set, optionally categorized to the
// "Bills" budget_category for this month so it shows up under that bucket
// in Plan/Log views. We don't auto-create that category if it doesn't
// exist — the bill becomes an uncategorized transaction in that case.

function MarkPaidSheet({ bill, paymentMethods, onClose, onSaved }) {
  const [amount, setAmount] = useState(bill.amount?.toString() || '')
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(today)
  const [paymentMethodId, setPaymentMethodId] = useState(
    paymentMethods.length > 0 ? paymentMethods[0].id : null
  )
  const [saving, setSaving] = useState(false)

  const handleConfirm = async () => {
    if (saving) return
    if (!amount) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()

    // Find this month's budget id. We'll use the bill's saved category_id
    // when present, falling back to the legacy "Bills" category lookup.
    const [txYearStr, txMonthStr] = date.split('-')
    const txYear = parseInt(txYearStr, 10)
    const txMonth = parseInt(txMonthStr, 10)
    const { data: budget } = await supabase.from('budgets').select('id')
      .eq('user_id', user.id).eq('month', txMonth).eq('year', txYear)
      .eq('is_active', true).order('created_at').limit(1).maybeSingle()

    let categoryId = null
    if (bill.category_id && budget) {
      // Bill has a category — but its UUID belongs to whatever budget it was
      // created in. Re-route to the equivalent in this transaction's budget.
      const { data: billCatRow } = await supabase.from('budget_categories')
        .select('name, parent_id').eq('id', bill.category_id).maybeSingle()
      if (billCatRow) {
        const { data: budgetCats } = await supabase.from('budget_categories')
          .select('id, name, parent_id').eq('budget_id', budget.id)
        const billParentName = billCatRow.parent_id
          ? ((budgetCats || []).find(c => c.id === billCatRow.parent_id)?.name || null)
          : null
        const equivalent = (budgetCats || []).find(c => {
          if ((c.name || '').toLowerCase().trim() !== (billCatRow.name || '').toLowerCase().trim()) return false
          if (!billParentName) return c.parent_id === null
          const candidateParentName = (budgetCats || []).find(p => p.id === c.parent_id)?.name || null
          return candidateParentName && candidateParentName.toLowerCase().trim() === billParentName.toLowerCase().trim()
        })
        if (equivalent) categoryId = equivalent.id
      }
    }

    if (!categoryId && budget) {
      // Legacy fallback — find a generic "Bills" category in this budget
      const { data: cats } = await supabase.from('budget_categories')
        .select('id, name').eq('budget_id', budget.id)
      const billsCat = (cats || []).find(c =>
        c.name.toLowerCase() === 'bills' ||
        c.name.toLowerCase() === 'bills & subscriptions' ||
        c.name.toLowerCase().includes('bill')
      )
      if (billsCat) categoryId = billsCat.id
    }

    await supabase.from('transactions').insert({
      user_id: user.id,
      budget_id: budget?.id || null,
      category_id: categoryId,
      bill_id: bill.id,
      amount: parseFloat(amount),
      type: 'expense',
      merchant_name: bill.name,
      transaction_date: date,
      payment_method_id: paymentMethodId || null,
      receipt_url: bill.receipt_url || null,
      is_business: false,
      is_charitable: false,
    })
    setSaving(false)
    onSaved()
  }

  return (
    <div
      style={styles.markPaidOverlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
    >
      <div style={styles.markPaidSheet}>
        <header style={styles.markPaidHeader}>
          <span style={styles.markPaidTitle}>Mark "{bill.name}" paid</span>
          <button
            type="button"
            className="btn-compact"
            style={styles.markPaidClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div style={styles.markPaidBody}>
          <p style={styles.markPaidIntro}>
            We'll record this as a transaction with <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>{bill.name}</strong> and link it to this bill.
          </p>

          <div style={styles.markPaidField}>
            <span style={styles.markPaidFieldLabel}>Amount</span>
            <input
              type="number" step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={styles.markPaidInput}
              aria-label="Amount"
            />
          </div>

          <div style={styles.markPaidField}>
            <span style={styles.markPaidFieldLabel}>Date paid</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={styles.markPaidInput}
              aria-label="Date paid"
            />
          </div>

          {paymentMethods.length > 0 && (
            <div style={styles.markPaidField}>
              <span style={styles.markPaidFieldLabel}>Paid with</span>
              <select
                value={paymentMethodId || ''}
                onChange={(e) => setPaymentMethodId(e.target.value || null)}
                style={styles.markPaidInput}
                aria-label="Payment method"
              >
                <option value="">— None —</option>
                {paymentMethods.map(pm => (
                  <option key={pm.id} value={pm.id}>
                    {pm.name}{pm.last_four ? ` · ${pm.last_four}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={styles.markPaidFooter}>
          <button
            type="button"
            className="btn-compact"
            style={styles.markPaidCancel}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            style={{
              ...styles.markPaidConfirm,
              opacity: (!amount || saving) ? 0.5 : 1,
              cursor: (!amount || saving) ? 'not-allowed' : 'pointer',
            }}
            onClick={handleConfirm}
            disabled={!amount || saving}
          >
            {saving ? 'Saving…' : 'Mark paid'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Past-month historical ledger
// ─────────────────────────────────────────────────────────────
//
// Replaces the entire ThisMonth UI when viewing a month that's already
// over. Shows what actually happened — income earned, money spent, net
// saved/overspent, full category breakdown — without any forward-looking
// "left to spend" or "no plan yet" framing. Reports is the place for
// printable PDFs; this is the at-a-glance historical view.

function PastMonthLedger({
  viewDate, isMobile, profile, partnerProfile,
  monthlyIncome, incomeSources, spent, transactions, categories, bills, paidBillIds,
  formatCurrency,
  onPrevMonth, onNextMonth,
  showMonthlySummary, onShowMonthlySummary, onCloseMonthlySummary,
}) {
  const monthName = viewDate.toLocaleString('default', { month: 'long' })
  const yearStr = viewDate.getFullYear()
  const net = monthlyIncome - spent
  const sharingFirst = partnerProfile ? firstName(partnerProfile.full_name) : null
  const greetingName = firstName(profile?.full_name) || 'there'

  // Build category rollups: parents with subs sum their children
  const parents = categories.filter(c => !c.parent_id)
  const subsByParent = {}
  categories.filter(c => c.parent_id).forEach(c => {
    if (!subsByParent[c.parent_id]) subsByParent[c.parent_id] = []
    subsByParent[c.parent_id].push(c)
  })

  // Spent per category (direct + via subcategories)
  const spentByCat = new Map()
  transactions.forEach(tx => {
    if (tx.category_id) {
      spentByCat.set(tx.category_id, (spentByCat.get(tx.category_id) || 0) + parseFloat(tx.amount || 0))
    }
  })

  const catRollups = parents.map(parent => {
    const subs = subsByParent[parent.id] || []
    const directSpent = spentByCat.get(parent.id) || 0
    const subsSpent = subs.reduce((s, sub) => s + (spentByCat.get(sub.id) || 0), 0)
    const total = directSpent + subsSpent
    return {
      parent,
      subs: subs.map(s => ({ sub: s, spent: spentByCat.get(s.id) || 0 })).filter(x => x.spent > 0),
      total,
      uncategorizedSpent: 0,
    }
  }).filter(r => r.total > 0)
  catRollups.sort((a, b) => b.total - a.total)

  // Uncategorized transactions (no category_id, or category_id not in this budget's categories)
  const validCatIds = new Set(categories.map(c => c.id))
  const uncategorizedSpent = transactions
    .filter(tx => !tx.category_id || !validCatIds.has(tx.category_id))
    .reduce((s, tx) => s + parseFloat(tx.amount || 0), 0)

  // Top merchants
  const merchantSpend = new Map()
  transactions.forEach(tx => {
    const m = tx.merchant_name || 'Unknown'
    merchantSpend.set(m, (merchantSpend.get(m) || 0) + parseFloat(tx.amount || 0))
  })
  const topMerchants = [...merchantSpend.entries()]
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5)

  // Bills paid this month (matched against paidBillIds)
  const billsPaidCount = bills.filter(b => paidBillIds.has(b.id)).length

  // Hero amount: net (positive = saved, negative = overspent)
  const netParts = splitCurrency(Math.abs(net), profile)
  const netLabel = net >= 0 ? 'Saved this month' : 'Overspent this month'
  const netColor = net >= 0 ? theme.colors.green : theme.colors.rose

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>

      {/* MonthlySummary modal */}
      {showMonthlySummary && (
        <MonthlySummary onDismiss={onCloseMonthlySummary} />
      )}

      {/* Page top: title + month nav */}
      <div style={isMobile ? styles.pageTopMobile : styles.pageTop}>
        {isMobile ? (
          <>
            <div style={styles.greetingMobile}>
              <h1 style={styles.greetingHi}>{monthName} {yearStr}</h1>
              <div style={styles.greetingMeta}>
                <span style={pastStyles.historicalTag}>
                  Historical
                </span>
                {sharingFirst && (
                  <>
                    <span style={atoms.pageMetaDot} />
                    <span style={styles.sharingMobile}>with {sharingFirst}</span>
                  </>
                )}
              </div>
            </div>
            <div style={pastStyles.monthNavMobile}>
              <button
                type="button"
                className="btn-compact"
                style={styles.monthNavBtn}
                onClick={onPrevMonth}
                aria-label="Previous month"
              >
                <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>‹</span>
              </button>
              <button
                type="button"
                className="btn-compact"
                style={styles.monthNavBtn}
                onClick={onNextMonth}
                aria-label="Next month"
              >
                <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>›</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={atoms.pageMeta}>
              <span style={{ color: theme.colors.plum, fontWeight: 600 }}>
                {monthName} {yearStr}
              </span>
              <span style={atoms.pageMetaDot} />
              <span style={pastStyles.historicalTag}>
                Historical · read-only
              </span>
              {sharingFirst && (
                <>
                  <span style={atoms.pageMetaDot} />
                  <span style={atoms.sharingPill}>
                    Sharing with {sharingFirst}
                  </span>
                </>
              )}
            </div>
            <div style={styles.headerRight}>
              <Link to="/report" className="btn-compact" style={styles.lastMonthBtn}>
                Full report
              </Link>
              <div style={styles.monthNav}>
                <button
                  type="button"
                  className="btn-compact"
                  style={styles.monthNavBtn}
                  onClick={onPrevMonth}
                  aria-label="Previous month"
                >
                  <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>‹</span>
                </button>
                <button
                  type="button"
                  className="btn-compact"
                  style={styles.monthNavBtn}
                  onClick={onNextMonth}
                  aria-label="Next month"
                >
                  <span aria-hidden="true" style={{ fontSize: '17px', lineHeight: 1 }}>›</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Hero: 3-stat ledger (Earned / Spent / Net) */}
      <section style={pastStyles.ledger}>
        <div style={pastStyles.ledgerCell}>
          <div style={pastStyles.ledgerLabel}>Earned</div>
          <div style={{ ...pastStyles.ledgerAmount, color: theme.colors.green }}>
            {formatCurrency(monthlyIncome)}
          </div>
          <div style={pastStyles.ledgerSub}>
            {incomeSources.length > 0
              ? `${incomeSources.length} source${incomeSources.length === 1 ? '' : 's'}`
              : 'No income recorded'}
          </div>
        </div>
        <div style={pastStyles.ledgerCell}>
          <div style={pastStyles.ledgerLabel}>Spent</div>
          <div style={pastStyles.ledgerAmount}>
            {formatCurrency(spent)}
          </div>
          <div style={pastStyles.ledgerSub}>
            {transactions.length} transaction{transactions.length === 1 ? '' : 's'}
          </div>
        </div>
        <div style={{ ...pastStyles.ledgerCell, ...pastStyles.ledgerCellNet }}>
          <div style={{ ...pastStyles.ledgerLabel, color: netColor }}>{netLabel}</div>
          <div style={{ ...pastStyles.ledgerAmount, color: netColor, display: 'flex', alignItems: 'baseline', gap: '3px' }}>
            <span style={{ ...pastStyles.ledgerCurrency, color: netColor }}>
              {net < 0 ? '−' : ''}{netParts.symbol}
            </span>
            {netParts.whole}
            <span style={{ ...pastStyles.ledgerCents, color: netColor }}>.{netParts.cents}</span>
          </div>
          <div style={pastStyles.ledgerSub}>
            {monthlyIncome > 0
              ? `${Math.round((net / monthlyIncome) * 100)}% of income`
              : '—'}
          </div>
        </div>
      </section>

      {/* Income sources list */}
      {incomeSources.length > 0 && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>Income</h2>
            <span style={pastStyles.sectionTotal}>{formatCurrency(monthlyIncome)} total</span>
          </div>
          <div style={atoms.card}>
            {incomeSources.map((src, idx) => {
              const last = idx === incomeSources.length - 1
              const monthly = monthlyEquivalentSimple(src)
              return (
                <div
                  key={idx}
                  style={{
                    ...pastStyles.line,
                    borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                  }}
                >
                  <div style={pastStyles.lineLabel}>
                    <span style={pastStyles.lineName}>{src.name}</span>
                    <span style={pastStyles.lineMeta}>
                      {friendlyFreq(src.frequency)}
                      {src.frequency && src.frequency !== 'monthly'
                        ? ` → ${formatCurrency(monthly)}/mo`
                        : ''}
                    </span>
                  </div>
                  <div style={{ ...pastStyles.lineAmount, color: theme.colors.green }}>
                    {formatCurrency(monthly)}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Spending by category */}
      {(catRollups.length > 0 || uncategorizedSpent > 0) && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>Spending by category</h2>
            <span style={pastStyles.sectionTotal}>{formatCurrency(spent)} total</span>
          </div>
          <div style={atoms.card}>
            {catRollups.map((rollup, idx) => {
              const last = idx === catRollups.length - 1 && uncategorizedSpent === 0
              const pct = spent > 0 ? (rollup.total / spent) * 100 : 0
              return (
                <div
                  key={rollup.parent.id}
                  style={{
                    borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                  }}
                >
                  <div style={pastStyles.catRow}>
                    <div style={pastStyles.catLabel}>
                      <span style={pastStyles.catName}>
                        {rollup.parent.name}
                      </span>
                      <div style={pastStyles.catBar}>
                        <div style={{
                          ...pastStyles.catBarFill,
                          width: `${Math.min(pct, 100)}%`,
                        }} />
                      </div>
                    </div>
                    <div style={pastStyles.catAmount}>
                      {formatCurrency(rollup.total)}
                      <span style={pastStyles.catPct}>{pct.toFixed(0)}%</span>
                    </div>
                  </div>
                  {rollup.subs.length > 0 && (
                    <div style={pastStyles.subList}>
                      {rollup.subs.map(({ sub, spent: subSpent }) => (
                        <div key={sub.id} style={pastStyles.subRow}>
                          <span style={pastStyles.subName}>
                            {sub.name}
                          </span>
                          <span style={pastStyles.subAmount}>{formatCurrency(subSpent)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
            {uncategorizedSpent > 0 && (
              <div style={pastStyles.catRow}>
                <div style={pastStyles.catLabel}>
                  <span style={{ ...pastStyles.catName, color: theme.colors.inkMuted, fontStyle: 'italic' }}>
                    Uncategorized
                  </span>
                  <div style={pastStyles.catBar}>
                    <div style={{
                      ...pastStyles.catBarFill,
                      width: `${Math.min((uncategorizedSpent / spent) * 100, 100)}%`,
                      background: theme.colors.inkQuiet,
                    }} />
                  </div>
                </div>
                <div style={pastStyles.catAmount}>
                  {formatCurrency(uncategorizedSpent)}
                  <span style={pastStyles.catPct}>
                    {((uncategorizedSpent / spent) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Top merchants */}
      {topMerchants.length > 0 && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>Top merchants</h2>
            <Link to="/log" className="btn-compact" style={styles.rowLink}>
              All transactions            </Link>
          </div>
          <div style={atoms.card}>
            {topMerchants.map((m, idx) => {
              const last = idx === topMerchants.length - 1
              return (
                <div
                  key={idx}
                  style={{
                    ...pastStyles.line,
                    borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                  }}
                >
                  <div style={pastStyles.lineLabel}>
                    <span style={pastStyles.lineName}>{m.name}</span>
                  </div>
                  <div style={pastStyles.lineAmount}>{formatCurrency(m.amount)}</div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Bills paid */}
      {billsPaidCount > 0 && (
        <section style={styles.row}>
          <div style={styles.rowHeader}>
            <h2 style={styles.rowTitle}>Bills paid</h2>
            <span style={pastStyles.sectionTotal}>
              {billsPaidCount} of {bills.length}
            </span>
          </div>
          <div style={atoms.card}>
            {bills.filter(b => paidBillIds.has(b.id)).map((bill, idx, arr) => {
              const last = idx === arr.length - 1
              return (
                <div
                  key={bill.id}
                  style={{
                    ...pastStyles.line,
                    borderBottom: last ? 'none' : `1px solid ${theme.colors.line}`,
                  }}
                >
                  <div style={pastStyles.lineLabel}>
                    <span style={pastStyles.lineName}>{bill.name}</span>
                    <span style={pastStyles.lineMeta}>
                      Due {bill.due_day} · ✓ Paid
                    </span>
                  </div>
                  <div style={pastStyles.lineAmount}>{formatCurrency(bill.amount)}</div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Footer link to full report */}
      <div style={isMobile ? styles.nwFooterMobile : styles.footerLine}>
        <div>
          <span style={{ color: theme.colors.inkMuted }}>
            Want a printable PDF or expense report?
          </span>
        </div>
        <Link to="/report" className="btn-compact" style={styles.nwLink}>
          Open Reports        </Link>
      </div>
    </div>
  )
}

// Helper: monthly equivalent for variable-frequency income (mirrors Plan.js)
function monthlyEquivalentSimple(source) {
  const a = parseFloat(source.amount || 0)
  switch (source.frequency) {
    case 'weekly':    return a * 4.33
    case 'biweekly':  return a * 2.17
    case 'quarterly': return a / 3
    case 'annual':    return a / 12
    default:          return a
  }
}

// ─── PastMonthLedger styles ──────────────────────────────────
const pastStyles = {
  historicalTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: theme.colors.cream3,
    color: theme.colors.plumSoft,
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  monthNavMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  ledger: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    background: theme.colors.white,
    border: `1px solid ${theme.colors.line}`,
    borderRadius: '14px',
    overflow: 'hidden',
    marginBottom: '32px',
    boxShadow: theme.shadows.card,
  },
  ledgerCell: {
    padding: '20px 22px',
    borderRight: `1px solid ${theme.colors.line}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  ledgerCellNet: {
    background: theme.colors.cream2,
    borderRight: 'none',
  },
  ledgerLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.colors.inkMuted,
  },
  ledgerAmount: {
    fontFamily: theme.fonts.display,
    fontWeight: 600,
    fontSize: '32px',
    lineHeight: 1,
    letterSpacing: '-0.025em',
    color: theme.colors.plum,
  },
  ledgerCurrency: {
    fontSize: '18px',
    fontWeight: 500,
    color: theme.colors.plumSoft,
    transform: 'translateY(-8px)',
  },
  ledgerCents: {
    fontSize: '18px',
    fontWeight: 500,
    color: theme.colors.plumSoft,
    transform: 'translateY(-6px)',
  },
  ledgerSub: {
    fontSize: '12px',
    color: theme.colors.inkMuted,
  },
  sectionTotal: {
    fontSize: '13px',
    color: theme.colors.inkMuted,
    fontWeight: 500,
  },
  line: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    gap: '14px',
  },
  lineLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  lineName: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: theme.colors.plum,
  },
  lineMeta: {
    fontSize: '12.5px',
    color: theme.colors.inkMuted,
  },
  lineAmount: {
    fontFamily: theme.fonts.display,
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '-0.015em',
    color: theme.colors.plum,
    flexShrink: 0,
  },
  catRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    gap: '14px',
  },
  catLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0,
    flex: 1,
  },
  catName: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: theme.colors.plum,
  },
  catBar: {
    height: '4px',
    background: theme.colors.cream3,
    borderRadius: '999px',
    overflow: 'hidden',
  },
  catBarFill: {
    height: '100%',
    background: theme.colors.green,
    borderRadius: '999px',
  },
  catAmount: {
    fontFamily: theme.fonts.display,
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '-0.015em',
    color: theme.colors.plum,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  },
  catPct: {
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    fontWeight: 600,
    color: theme.colors.inkMuted,
    letterSpacing: '0.04em',
    minWidth: '32px',
    textAlign: 'right',
  },
  subList: {
    background: theme.colors.cream2,
    padding: '4px 0',
    borderTop: `1px solid ${theme.colors.line}`,
  },
  subRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 18px 6px 36px',
    gap: '14px',
  },
  subName: {
    fontSize: '13px',
    color: theme.colors.plumSoft,
  },
  subAmount: {
    fontSize: '13px',
    fontWeight: 500,
    color: theme.colors.plumSoft,
  },
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function PaceTag({ onPace, overPace, budgetTotal }) {
  if (budgetTotal === 0) return null
  if (overPace) {
    return (
      <span style={{ ...atoms.pillOrange }}>
        Over pace
      </span>
    )
  }
  if (onPace) {
    return (
      <span style={atoms.pillGreen}>
        On pace
      </span>
    )
  }
  return (
    <span style={atoms.pillOrange}>
      Watching
    </span>
  )
}

function PlanCard({ kicker, name, detail, progress, progressLabel, color }) {
  const fillColor = color === 'purple' ? theme.colors.purple : theme.colors.green
  const pct = Math.max(0, Math.min(progress * 100, 100))
  return (
    <div style={styles.planCard}>
      <div style={styles.planTop}>
        <span style={styles.planKicker}>{kicker}</span>
        <span style={{ ...styles.planCheck, fontSize: '11px', lineHeight: 1 }} aria-hidden="true">
          ✓
        </span>
      </div>
      <div style={styles.planName}>{name}</div>
      <div style={styles.planDetail}>{detail}</div>
      <div style={styles.planProgress}>
        <span>{progressLabel}</span>
        <span style={styles.planAmt}>{Math.round(pct)}%</span>
      </div>
      <div style={styles.planBar}>
        <div style={{ ...styles.planBarFill, width: `${pct}%`, background: fillColor }} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function splitCurrency(amount, profile) {
  // Use Intl to get a localised string, then split into parts so we can
  // typeset the symbol/cents smaller around a giant integer in Fraunces.
  // Currency comes from the context, not the profile snapshot — so it
  // updates reactively when the user changes currency in Settings.
  const cur = getCurrentCurrency()
  const code   = cur.code
  const locale = cur.locale
  const symbol = cur.symbol
  let formatted = ''
  try {
    formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0)
  } catch (e) {
    formatted = `${symbol}${(amount || 0).toFixed(2)}`
  }
  // Strip currency symbol(s) — locale dependent
  const numericMatch = formatted.match(/-?[\d.,\s]+/)
  const numeric = numericMatch ? numericMatch[0].trim() : formatted
  const sign = (amount || 0) < 0 ? '-' : ''
  // Split decimal — try comma or period
  const parts = numeric.replace(/^-/, '').split(/[.,](?=\d{2}$)/)
  const whole = parts[0] || '0'
  const cents = (parts[1] || '00').padEnd(2, '0').slice(0, 2)
  return { symbol, whole: sign + whole, cents }
}

function daysUntil(dueDay, today) {
  const day = today.getDate()
  if (dueDay >= day) return dueDay - day
  // Wraps to next month
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  return lastDay - day + dueDay
}

function friendlyFreq(f) {
  if (!f || f === 'monthly') return 'Monthly'
  if (f === 'one_time') return 'One time'
  if (f === 'semi_monthly') return 'Bi-monthly'
  return f.charAt(0).toUpperCase() + f.slice(1)
}

function dueLabel(days) {
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  return `Due in ${days} days`
}

// "by today / tomorrow / Tue, Jul 15" for the Money to move section.
function byDateLabel(days, today) {
  if (days <= 0) return 'today'
  if (days === 1) return 'tomorrow'
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function nextMonthShort(today, dueDay) {
  // If the due day has already passed this month, the bill date label uses next month.
  const d = today.getDate()
  const targetMonthIdx = dueDay >= d ? today.getMonth() : (today.getMonth() + 1) % 12
  return SHORT_MONTHS[targetMonthIdx]
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  page: {
    maxWidth: '980px',
    margin: '0 auto',
    width: '100%',
    padding: '16px 32px 120px',
    fontFamily: f.body,
    color: c.plum,
  },
  pageMobile: {
    width: '100%',
    paddingBottom: '40px',
    fontFamily: f.body,
    color: c.plum,
  },

  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '60px',
    color: c.inkMuted,
    fontSize: '14px',
  },

  // ─── Page top ────────────────────────────────────────
  pageTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '36px',
    gap: '14px',
    flexWrap: 'wrap',
  },
  pageTopMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 22px 24px',
    gap: '12px',
  },
  greetingMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  greetingHi: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '26px',
    letterSpacing: '-0.025em',
    color: c.plum,
    margin: 0,
  },
  greetingMeta: {
    fontSize: '12.5px',
    color: c.inkMuted,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  sharingMobile: {
    color: c.green,
    fontWeight: 600,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
  },
  lastMonthBtn: {
    background: 'transparent',
    color: c.plumSoft,
    border: `1px solid ${c.line}`,
    padding: '7px 12px',
    borderRadius: '8px',
    fontFamily: f.body,
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  historyBtn: {
    background: 'transparent',
    border: `1px solid ${c.line}`,
    padding: '8px 12px',
    borderRadius: '10px',
    color: c.plumSoft,
    cursor: 'pointer',
    fontFamily: f.body,
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  monthNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  monthNavBtn: {
    background: 'transparent',
    border: `1px solid ${c.line}`,
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: c.plumSoft,
    transition: 'background 120ms ease, border-color 120ms ease',
  },

  // ─── Checklist ───────────────────────────────────────
  checklist: {
    background: c.white,
    borderRadius: '14px',
    border: `1px solid ${c.line}`,
    padding: '20px',
    marginBottom: '28px',
    boxShadow: theme.shadows.card,
    margin: '0 16px 28px',
  },
  checklistHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    gap: '12px',
  },
  checklistTitle: {
    fontFamily: f.display,
    fontSize: '17px',
    fontWeight: 600,
    color: c.plum,
    marginBottom: '2px',
    letterSpacing: '-0.01em',
  },
  checklistProgress: {
    fontSize: '12.5px',
    color: c.inkMuted,
  },
  checklistResume: {
    background: 'transparent',
    border: `1px solid ${c.line}`,
    padding: '6px 12px',
    borderRadius: '8px',
    fontFamily: f.body,
    fontSize: '12.5px',
    fontWeight: 600,
    color: c.green,
    cursor: 'pointer',
  },
  checklistDismiss: {
    background: 'transparent',
    border: 'none',
    padding: '4px',
    color: c.inkMuted,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    fontSize: '18px',
    lineHeight: 1,
  },
  checklistBar: {
    height: '4px',
    background: c.cream3,
    borderRadius: '2px',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  checklistBarFill: {
    height: '100%',
    background: c.green,
    borderRadius: '2px',
    transition: 'width 0.4s ease',
  },
  checklistItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  checklistCheck: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    transition: 'background 200ms ease, border-color 200ms ease',
  },
  checklistItemText: {
    flex: 1,
    minWidth: 0,
  },
  checklistItemLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: c.plum,
    marginBottom: '1px',
  },
  checklistItemSub: {
    fontSize: '12.5px',
    color: c.inkMuted,
  },
  checklistCta: {
    fontSize: '12.5px',
    color: c.green,
    textDecoration: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },

  // ─── Hero ────────────────────────────────────────────
  hero: {
    marginBottom: '48px',
  },
  heroMobile: {
    margin: '0 16px 18px',
    padding: '22px 22px 18px',
    background: theme.gradients.heroCard,
    border: `1px solid ${c.line}`,
    borderRadius: '22px',
    boxShadow: theme.shadows.heroCard,
  },
  heroLabel: {
    fontSize: '12.5px',
    fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '14px',
  },
  heroLabelRowMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  heroAmountMobile: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '56px',
    lineHeight: 1,
    letterSpacing: '-0.035em',
    color: c.plum,
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    marginBottom: '10px',
  },
  heroCurrencyMobile: {
    fontSize: '26px',
    fontWeight: 500,
    color: c.plumSoft,
    transform: 'translateY(-14px)',
  },
  heroSub: {
    marginTop: '14px',
    fontSize: '14.5px',
    color: c.inkMuted,
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap',
  },
  heroSubMobile: {
    fontSize: '13.5px',
    color: c.inkMuted,
    marginBottom: '14px',
  },

  progressTrack: {
    marginTop: '22px',
    height: '6px',
    background: c.cream3,
    borderRadius: '999px',
    overflow: 'hidden',
    position: 'relative',
  },
  progressFill: {
    height: '100%',
    background: theme.gradients.progress,
    borderRadius: '999px',
    transition: 'width 800ms cubic-bezier(.4,.1,.2,1)',
  },
  progressMarker: {
    position: 'absolute',
    top: '-3px',
    width: '2px',
    height: '12px',
    background: c.plumSoft,
    opacity: 0.35,
    borderRadius: '1px',
  },
  heroFoot: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: c.inkMuted,
  },

  // ─── Empty budget CTA ────────────────────────────────
  emptyBudget: {
    margin: '0 16px 28px',
    padding: '18px 20px',
    background: c.cream2,
    border: `1px dashed ${c.lineStrong}`,
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  timeToPlan: {
    margin: '0 16px 20px',
    padding: '16px 18px',
    background: c.greenSoft,
    border: `1px solid rgba(22, 163, 74, 0.18)`,
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  timeToPlanText: { minWidth: 0, flex: 1 },
  timeToPlanTitle: {
    fontSize: '15.5px',
    fontWeight: 600,
    color: c.plum,
    marginBottom: '2px',
  },
  timeToPlanSub: {
    fontSize: '13px',
    color: c.plumSoft,
  },
  timeToPlanActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  timeToPlanCta: {
    fontSize: '13.5px',
    fontWeight: 600,
    color: '#FFFFFF',
    background: c.green,
    textDecoration: 'none',
    padding: '9px 16px',
    borderRadius: '10px',
    whiteSpace: 'nowrap',
  },
  timeToPlanDismiss: {
    fontSize: '13px',
    fontWeight: 600,
    color: c.plumSoft,
    background: 'transparent',
    border: 'none',
    padding: '9px 10px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyBudgetTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: c.plum,
    marginBottom: '2px',
  },
  emptyBudgetSub: {
    fontSize: '13px',
    color: c.inkMuted,
  },
  emptyBudgetCta: {
    fontSize: '13.5px',
    fontWeight: 600,
    color: c.green,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },

  // ─── Section row ─────────────────────────────────────
  row: {
    marginBottom: '28px',
    padding: '0 16px',
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: '14px',
    padding: '0 6px',
  },
  rowTitle: {
    fontFamily: f.body,
    fontSize: '14px',
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.005em',
    margin: 0,
  },
  rowLink: {
    fontSize: '13px',
    fontWeight: 600,
    color: c.green,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },

  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  // ─── Bills ───────────────────────────────────────────
  billRow: {
    display: 'grid',
    gridTemplateColumns: '56px 1fr auto auto auto',
    gap: '18px',
    alignItems: 'center',
    padding: '16px 20px',
    transition: 'background 100ms ease',
  },
  markPaidBtn: {
    background: 'transparent',
    color: c.green,
    border: `1px solid rgba(22, 163, 74, 0.25)`,
    padding: '6px 12px',
    borderRadius: '8px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  // Chunk 7 — Money to move
  moveRow: {
    padding: '16px 20px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  moveHead: {
    fontSize: '14.5px', color: c.plumSoft, lineHeight: 1.5,
  },
  moveAmount: {
    fontFamily: f.body, fontWeight: 700, color: c.plum,
    fontVariantNumeric: 'tabular-nums',
  },
  moveAccount: { fontWeight: 600, color: c.plum },
  moveBills: {
    display: 'flex', flexWrap: 'wrap', gap: '6px',
  },
  moveBillChip: {
    background: c.cream2, color: c.plumSoft,
    borderRadius: '7px', padding: '4px 9px',
    fontSize: '12.5px', fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
  markPaidOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(30, 16, 64, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 900, fontFamily: f.body,
  },
  markPaidSheet: {
    background: c.cream,
    borderRadius: '20px 20px 0 0',
    width: '100%', maxWidth: '460px', maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(30, 16, 64, 0.2)',
  },
  markPaidHeader: {
    padding: '14px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${c.line}`,
  },
  markPaidTitle: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '17px', letterSpacing: '-0.015em',
    color: c.plum,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    flex: 1, marginRight: '12px',
  },
  markPaidClose: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: 'rgba(30, 16, 64, 0.05)',
    display: 'grid', placeItems: 'center',
    cursor: 'pointer', color: c.plum, border: 'none',
    flexShrink: 0,
    fontSize: '20px', lineHeight: 1,
  },
  markPaidBody: {
    flex: 1, overflowY: 'auto',
    padding: '22px 22px 12px',
  },
  markPaidIcon: {
    width: '52px', height: '52px',
    borderRadius: '14px',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center',
    margin: '0 auto 14px',
  },
  markPaidIntro: {
    fontSize: '14px', color: c.inkMuted,
    lineHeight: 1.5, textAlign: 'center',
    margin: '0 0 22px',
  },
  markPaidField: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    marginBottom: '14px',
  },
  markPaidFieldLabel: {
    fontSize: '12px', fontWeight: 600,
    color: c.inkMuted, letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  markPaidInput: {
    width: '100%',
    padding: '11px 14px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14.5px',
    color: c.plum, outline: 'none',
    boxSizing: 'border-box',
  },
  markPaidFooter: {
    padding: '12px 22px calc(env(safe-area-inset-bottom, 16px) + 14px)',
    borderTop: `1px solid ${c.line}`,
    display: 'flex', gap: '10px',
  },
  markPaidCancel: {
    flex: 1,
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`,
    padding: '12px',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
    cursor: 'pointer',
  },
  markPaidConfirm: {
    flex: 2,
    background: c.green, color: '#FFFFFF',
    border: 'none',
    padding: '12px',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px',
  },
  listCardMobile: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    cursor: 'pointer',
  },
  billDate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    lineHeight: 1.05,
    width: '44px',
    flexShrink: 0,
  },
  billDateNum: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '24px',
    color: c.plum,
    letterSpacing: '-0.02em',
  },
  billDateMo: {
    fontSize: '10px',
    fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginTop: '2px',
  },
  billInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    flex: 1,
    minWidth: 0,
  },
  billName: {
    fontSize: '15px',
    fontWeight: 600,
    color: c.plum,
  },
  billMeta: {
    fontSize: '13px',
    color: c.inkMuted,
  },
  billDue: {
    fontSize: '13px',
  },
  billAmount: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '18px',
    color: c.plum,
    letterSpacing: '-0.02em',
    flexShrink: 0,
  },

  // ─── Categories to watch ─────────────────────────────
  catRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 140px 90px',
    gap: '18px',
    alignItems: 'center',
    padding: '14px 20px',
  },
  catCardMobile: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  catNameWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  catLabel: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: c.plum,
  },
  catRowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  catName: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: c.plum,
  },
  catAmount: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '15px',
    color: c.plum,
    letterSpacing: '-0.015em',
  },
  catBar: {
    height: '4px',
    background: c.cream3,
    borderRadius: '999px',
    overflow: 'hidden',
  },
  catBarFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 600ms ease',
  },
  catSpend: {
    textAlign: 'right',
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '-0.015em',
    color: c.plum,
  },
  catStatus: {
    textAlign: 'right',
    fontSize: '12.5px',
    fontWeight: 600,
  },
  catStatusMobile: {
    fontSize: '12px',
    fontWeight: 600,
  },

  // ─── Plan grid ───────────────────────────────────────
  planGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
  },
  planCard: {
    padding: '18px 20px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    boxShadow: theme.shadows.card,
  },
  planTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  planKicker: {
    fontSize: '11.5px',
    fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  planCheck: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: c.greenSoft,
    color: c.green,
    display: 'grid',
    placeItems: 'center',
  },
  planName: {
    fontSize: '15.5px',
    fontWeight: 600,
    color: c.plum,
    marginBottom: '4px',
  },
  planDetail: {
    fontSize: '13px',
    color: c.inkMuted,
    marginBottom: '14px',
  },
  planProgress: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: c.plumSoft,
    marginBottom: '8px',
  },
  planAmt: {
    fontFamily: f.display,
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.01em',
  },
  planBar: {
    height: '4px',
    background: c.cream3,
    borderRadius: '999px',
    overflow: 'hidden',
  },
  planBarFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 600ms ease',
  },

  // ─── Footer line ─────────────────────────────────────
  footerLine: {
    marginTop: '40px',
    padding: '18px 22px 0',
    borderTop: `1px solid ${c.line}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '13.5px',
    color: c.inkMuted,
    flexWrap: 'wrap',
    gap: '10px',
  },
  nwFooterMobile: {
    margin: '22px 16px 8px',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.5)',
    border: `1px dashed ${c.lineStrong}`,
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: c.inkMuted,
  },
  nwAmount: {
    fontFamily: f.display,
    fontWeight: 600,
    color: c.plum,
    fontSize: '15px',
    marginLeft: '4px',
  },
  nwLink: {
    color: c.green,
    textDecoration: 'none',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
  },

  // ─── FAB ─────────────────────────────────────────────
  fab: {
    position: 'fixed',
    right: '32px',
    bottom: '32px',
    background: c.plum,
    color: c.cream,
    border: 'none',
    height: '52px',
    padding: '0 22px 0 18px',
    borderRadius: '999px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: f.body,
    fontWeight: 600,
    fontSize: '14.5px',
    cursor: 'pointer',
    boxShadow: theme.shadows.fab,
    transition: 'transform 140ms ease, box-shadow 140ms ease',
    zIndex: 50,
  },
}
